<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import CommentAutomationCard from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CommentAutomation/CommentAutomationCard.vue';
import CommentAutomationEditor from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CommentAutomation/CommentAutomationEditor.vue';

const store = useStore();

const inboxes = useMapGetter('inboxes/getInboxes');
const automations = useMapGetter('commentAutomations/getInstagramAutomations');
const uiFlags = useMapGetter('commentAutomations/getUIFlags');

const isFetching = computed(() => uiFlags.value.isFetching);
const isEditing = ref(false);
const isSyncingAll = ref(false);
const isDeleting = ref(false);
const selectedAutomation = ref(null);
const confirmDeleteDialogRef = ref(null);

const instagramInboxes = computed(() => {
  return inboxes.value.filter(
    inbox =>
      inbox.channel_type === 'Channel::Instagram' ||
      (inbox.channel_type === 'Channel::FacebookPage' && inbox.instagram_id)
  );
});

const currentInbox = computed(() => {
  return (
    instagramInboxes.value.find(i => i.channel_type === 'Channel::Instagram') ||
    instagramInboxes.value[0]
  );
});

const filteredAutomations = computed(() => {
  return automations.value;
});

const handleCreate = () => {
  selectedAutomation.value = null;
  isEditing.value = true;
};

const handleEdit = automation => {
  selectedAutomation.value = automation;
  isEditing.value = true;
};

const handleDeleteClick = automation => {
  selectedAutomation.value = automation;
  confirmDeleteDialogRef.value?.open();
};

const handleConfirmDelete = async () => {
  if (!selectedAutomation.value?.id) return;
  isDeleting.value = true;
  try {
    await store.dispatch('commentAutomations/delete', selectedAutomation.value.id);
    useAlert('Comment automation deleted successfully');
    confirmDeleteDialogRef.value?.close();
  } catch (error) {
    useAlert('Error deleting comment automation');
  } finally {
    isDeleting.value = false;
  }
};

const handleToggleActive = async automation => {
  await store.dispatch('commentAutomations/toggleActive', automation.id);
};

const handleSyncAll = async () => {
  if (isSyncingAll.value) return;
  isSyncingAll.value = true;
  try {
    const res = await store.dispatch('commentAutomations/sync', { inboxId: currentInbox.value?.id });
    useAlert(res.message || 'Comments synced successfully!');
  } catch (error) {
    useAlert('Error syncing comments');
  } finally {
    isSyncingAll.value = false;
  }
};

const handleSyncAutomation = async automation => {
  try {
    const res = await store.dispatch('commentAutomations/sync', { id: automation.id });
    useAlert(res.message || `Synced comments for ${automation.name}`);
  } catch (error) {
    useAlert(`Error syncing comments for ${automation.name}`);
  }
};

const handleSaveSuccess = () => {
  isEditing.value = false;
  store.dispatch('commentAutomations/get');
};

onMounted(() => {
  store.dispatch('commentAutomations/get');
});
</script>

<template>
  <!-- Full Screen Editor View -->
  <div v-if="isEditing && currentInbox" class="w-full h-full p-6 bg-n-background dark:bg-n-black overflow-hidden flex flex-col">
    <CommentAutomationEditor
      :inbox="currentInbox"
      platform="instagram"
      :initial-data="selectedAutomation"
      @save="handleSaveSuccess"
      @cancel="isEditing = false"
    />
  </div>

  <!-- Campaign List View -->
  <CampaignLayout
    v-else
    header-title="Instagram Comment Automations"
    :button-label="currentInbox ? 'Create campaign' : ''"
    @click="handleCreate"
  >
    <template #action>
      <Button
        v-if="filteredAutomations.length > 0"
        :icon="isSyncingAll ? 'i-lucide-loader-2 animate-spin' : 'i-lucide-refresh-cw'"
        label="Sync comments"
        size="sm"
        variant="ghost"
        class="mr-2"
        @click="handleSyncAll"
      />
    </template>

    <!-- No Account Warning -->
    <div
      v-if="instagramInboxes.length === 0"
      class="flex flex-col items-center justify-center p-12 text-center bg-n-surface-1 rounded-2xl border border-n-weak my-8"
    >
      <div class="w-14 h-14 rounded-full bg-orange-500/10 text-orange-500 flex items-center justify-center text-2xl mb-4">
        <span class="i-woot-instagram" />
      </div>
      <h2 class="text-base font-semibold text-n-slate-12 mb-1">Instagram Account Not Connected</h2>
      <p class="text-xs text-n-slate-11 max-w-sm mb-6">
        Connect your Instagram Business account to start setting up automated keyword DM and comment replies.
      </p>
      <router-link :to="{ name: 'settings_inbox_new' }">
        <Button label="Connect Instagram Account" icon="i-lucide-plus" size="sm" />
      </router-link>
    </div>

    <!-- Loading State -->
    <div v-if="isFetching" class="flex justify-center items-center py-16 text-n-slate-11">
      <Spinner />
    </div>

    <!-- Automations List -->
    <div v-else-if="filteredAutomations.length > 0" class="flex flex-col gap-3 py-2">
      <CommentAutomationCard
        v-for="automation in filteredAutomations"
        :key="automation.id"
        :automation="automation"
        @edit="handleEdit"
        @delete="handleDeleteClick"
        @toggle-active="handleToggleActive"
        @sync="handleSyncAutomation"
      />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="currentInbox"
      class="flex flex-col items-center justify-center py-16 text-center"
    >
      <div class="w-16 h-16 rounded-2xl bg-n-brand/10 flex items-center justify-center text-3xl mb-4 border border-n-brand/20 text-n-brand">
        <span class="i-lucide-sparkles" />
      </div>
      <h3 class="text-base font-semibold text-n-slate-12 mb-1">
        No Instagram Comment Automations Yet
      </h3>
      <p class="text-xs text-n-slate-11 max-w-sm mb-6">
        Automatically send direct messages and public replies when users comment specific keywords on your posts or reels.
      </p>
      <Button
        label="Create Your First Campaign"
        icon="i-lucide-plus"
        size="sm"
        @click="handleCreate"
      />
    </div>

    <Dialog
      ref="confirmDeleteDialogRef"
      type="alert"
      title="Delete Comment Automation"
      description="Are you sure you want to delete this comment automation? This action cannot be undone."
      confirm-button-label="Delete"
      :is-loading="isDeleting"
      @confirm="handleConfirmDelete"
    />
  </CampaignLayout>
</template>
